/**
 * Semantic chat style tokens, legacy migration, and expansion to shadcn CSS variables.
 */
import {
	CONTRAST_PAIRS,
	contrastRatio,
	parseColor,
	pickReadableText,
} from './contrast';

export const SEMANTIC_STYLE_DEFAULTS: Record< string, string > = {
	'--chat-background': '#ffffff',
	'--chat-overlay': '#ffffff',
	'--chat-foreground': '#252525',
	'--chat-border': '#ebebeb',
	'--chat-muted-foreground': '#6b6b6b',
	'--chat-error': '#e7000b',
	'--chat-error-foreground': '#ffffff',
	'--chat-message-user-bg': '#343434',
	'--chat-message-user-fg': '#ffffff',
	'--chat-message-assistant-bg': '#f0f0f0',
	'--chat-message-assistant-fg': '#252525',
	'--chat-composer-bg': '#ffffff',
	'--chat-composer-fg': '#252525',
	'--chat-composer-border': '#ebebeb',
	'--chat-composer-placeholder': '#6b6b6b',
	'--chat-composer-submit-bg': '#343434',
	'--chat-composer-submit-fg': '#ffffff',
	'--launcher-background': '#0a0a0a',
	'--launcher-foreground': '#fafafa',
	'--launcher-muted': '#a3a3a3',
	'--chat-font-size-message': '0.90625rem',
	'--chat-font-size-heading': '1.0625rem',
	'--chat-font-size-composer': '0.90625rem',
	'--chat-font-size-footer': '0.75rem',
	'--chat-font-size-code': '0.88em',
	'--chat-font-mono': '"Geist Mono", ui-monospace, monospace',
	'--chat-line-height-text': '1.5',
	'--chat-line-height-heading': '1.4',
	'--chat-message-gap': '1.125rem',
	'--radius': '0.625rem',
	'--bot-placeholder-height': '10rem',
};

/** Legacy shadcn keys → semantic keys (only when semantic is unset). */
export const LEGACY_TO_SEMANTIC: Record< string, string > = {
	'--background': '--chat-background',
	'--popover': '--chat-overlay',
	'--foreground': '--chat-foreground',
	'--border': '--chat-border',
	'--muted-foreground': '--chat-muted-foreground',
	'--destructive': '--chat-error',
	'--destructive-foreground': '--chat-error-foreground',
	'--primary': '--chat-message-user-bg',
	'--primary-foreground': '--chat-message-user-fg',
	'--muted': '--chat-message-assistant-bg',
	'--input': '--chat-composer-border',
	'--chat-body-font-size-p': '--chat-font-size-message',
	'--chat-body-line-height-text': '--chat-line-height-text',
	'--chat-body-line-height-heading': '--chat-line-height-heading',
	'--chat-body-font-size-code': '--chat-font-size-code',
};

/** Keys from the previous style editor that are removed on convert (not shown in UI). */
export const LEGACY_ONLY_KEYS = new Set( [
	...Object.keys( LEGACY_TO_SEMANTIC ),
	'--popover-foreground',
	'--secondary',
	'--secondary-foreground',
	'--accent',
	'--accent-foreground',
	'--ring',
	'--card',
	'--card-foreground',
	'--chat-body-font-size-h1',
	'--chat-body-font-size-h2',
	'--chat-body-font-size-h3',
	'--chat-body-font-size-h4',
	'--chat-body-font-size-h5',
	'--chat-body-font-size-h6',
	'--chat-body-font-size-li',
	'--chat-body-font-size-blockquote',
	'--font-size-base',
	'--font-size-lg',
	'--font-size-xl',
	'--font-size-2xl',
	'--font-size-3xl',
	'--font-size-4xl',
	'--font-size-5xl',
	'--font-size-6xl',
	'--font-size-7xl',
	'--font-size-8xl',
	'--font-size-9xl',
	'--shadow-xl',
	'--shadow-lg',
	'--shadow-md',
	'--shadow-sm',
	'--shadow-xs',
	'--shadow-inner',
] );

/**
 * Parse the stored style config JSON into CSS custom properties. Keys get a
 * `--` prefix if missing; non-string values and invalid JSON are dropped.
 *
 * @param raw Stored JSON string.
 */
export function parseStyleConfigJson( raw: string ): Record< string, string > {
	const trimmed = raw.trim();
	if ( ! trimmed ) {
		return {};
	}
	try {
		const parsed = JSON.parse( trimmed ) as unknown;
		if (
			! parsed ||
			typeof parsed !== 'object' ||
			Array.isArray( parsed )
		) {
			return {};
		}
		const result: Record< string, string > = {};
		Object.entries( parsed ).forEach( ( [ key, value ] ) => {
			if ( typeof key === 'string' && typeof value === 'string' ) {
				result[ key.startsWith( '--' ) ? key : `--${ key }` ] = value;
			}
		} );
		return result;
	} catch {
		return {};
	}
}

/**
 * Copy legacy keys to their semantic successors where the successor is not
 * set yet, and derive composer and heading values from older keys.
 *
 * @param stored Parsed style config.
 */
export function migrateStyleConfig(
	stored: Record< string, string >
): Record< string, string > {
	const result = { ...stored };
	Object.entries( LEGACY_TO_SEMANTIC ).forEach( ( [ legacy, semantic ] ) => {
		const legacyValue = stored[ legacy ];
		if ( ! ( semantic in stored ) && legacyValue !== undefined ) {
			result[ semantic ] = legacyValue;
		}
	} );
	if (
		! ( '--chat-message-assistant-fg' in result ) &&
		( '--foreground' in stored || '--chat-foreground' in result )
	) {
		result[ '--chat-message-assistant-fg' ] =
			result[ '--chat-foreground' ] ?? stored[ '--foreground' ] ?? '';
	}
	if (
		! ( '--chat-composer-bg' in result ) &&
		'--chat-background' in result
	) {
		result[ '--chat-composer-bg' ] = result[ '--chat-background' ];
	}
	if (
		! ( '--chat-composer-fg' in result ) &&
		'--chat-foreground' in result
	) {
		result[ '--chat-composer-fg' ] = result[ '--chat-foreground' ];
	}
	if (
		! ( '--chat-composer-submit-bg' in result ) &&
		'--chat-message-user-bg' in result
	) {
		result[ '--chat-composer-submit-bg' ] =
			result[ '--chat-message-user-bg' ];
	}
	if (
		! ( '--chat-composer-submit-fg' in result ) &&
		'--chat-message-user-fg' in result
	) {
		result[ '--chat-composer-submit-fg' ] =
			result[ '--chat-message-user-fg' ];
	}
	if ( ! ( '--chat-font-size-heading' in stored ) ) {
		const headingFallbacks = [
			'--chat-body-font-size-h3',
			'--chat-body-font-size-h2',
			'--chat-body-font-size-h1',
		];
		for ( const legacyHeading of headingFallbacks ) {
			const legacyValue = stored[ legacyHeading ];
			if ( legacyValue !== undefined ) {
				result[ '--chat-font-size-heading' ] = legacyValue;
				break;
			}
		}
	}
	return result;
}

/**
 * Number of stored keys that are legacy or not in the schema, used by the
 * backend editor to offer a conversion.
 *
 * @param stored     Parsed style config.
 * @param schemaKeys Keys of the current schema.
 */
export function countLegacyStyleKeys(
	stored: Record< string, string >,
	schemaKeys: Set< string >
): number {
	return Object.keys( stored ).filter(
		( key ) => ! schemaKeys.has( key ) || LEGACY_ONLY_KEYS.has( key )
	).length;
}

/**
 * Replace legacy stored keys with semantic-only sparse config.
 * @param stored
 * @param schemaKeys
 */
export function convertLegacyStyleConfig(
	stored: Record< string, string >,
	schemaKeys: Set< string >
): { config: Record< string, string >; removedCount: number } {
	const migrated = migrateStyleConfig( stored );
	const result: Record< string, string > = {};

	schemaKeys.forEach( ( key ) => {
		const value = migrated[ key ];
		if ( value === undefined ) {
			return;
		}

		const fromStored = key in stored;
		const fromLegacy = Object.entries( LEGACY_TO_SEMANTIC ).some(
			( [ legacy, semantic ] ) => semantic === key && legacy in stored
		);
		const fromLegacyHeading =
			key === '--chat-font-size-heading' &&
			[
				'--chat-body-font-size-h1',
				'--chat-body-font-size-h2',
				'--chat-body-font-size-h3',
			].some( ( legacyHeading ) => legacyHeading in stored );
		const differsFromDefault = value !== SEMANTIC_STYLE_DEFAULTS[ key ];

		if (
			fromStored ||
			fromLegacy ||
			fromLegacyHeading ||
			differsFromDefault
		) {
			result[ key ] = value;
		}
	} );

	const removedCount = countLegacyStyleKeys( stored, schemaKeys );

	return { config: result, removedCount };
}

function remToNumber( value: string ): number | null {
	const match = value.trim().match( /^([\d.]+)rem$/ );
	if ( ! match?.[ 1 ] ) {
		return null;
	}
	const n = parseFloat( match[ 1 ] );
	return Number.isFinite( n ) ? n : null;
}

function scaleRem( value: string, factor: number ): string {
	const n = remToNumber( value );
	if ( n === null ) {
		return value;
	}
	return `${ Math.round( n * factor * 10000 ) / 10000 }rem`;
}

/**
 * Merge defaults + stored, migrate legacy keys, derive shadcn + markdown typography vars.
 * @param stored
 */
export function expandStyleConfig(
	stored: Record< string, string >
): Record< string, string > {
	const migrated = migrateStyleConfig( stored );
	const config = { ...SEMANTIC_STYLE_DEFAULTS, ...migrated };

	const req = ( key: keyof typeof SEMANTIC_STYLE_DEFAULTS ): string => {
		const value =
			( config as Record< string, string > )[ key ] ??
			SEMANTIC_STYLE_DEFAULTS[ key ];
		return value ?? '';
	};

	const messageSize = req( '--chat-font-size-message' );
	const headingSize = req( '--chat-font-size-heading' );

	const expanded: Record< string, string > = {
		...stored,
		...config,
		'--background': req( '--chat-background' ),
		'--foreground': req( '--chat-foreground' ),
		'--card': req( '--chat-background' ),
		'--card-foreground': req( '--chat-foreground' ),
		'--popover': req( '--chat-overlay' ),
		'--popover-foreground': req( '--chat-foreground' ),
		'--primary': req( '--chat-message-user-bg' ),
		'--primary-foreground': req( '--chat-message-user-fg' ),
		'--muted': req( '--chat-message-assistant-bg' ),
		'--muted-foreground': req( '--chat-muted-foreground' ),
		'--border': req( '--chat-border' ),
		'--input': req( '--chat-composer-border' ),
		'--destructive': req( '--chat-error' ),
		'--destructive-foreground': req( '--chat-error-foreground' ),
		'--chat-body-font-size-p': messageSize,
		'--chat-body-font-size-li': messageSize,
		'--chat-body-font-size-blockquote': messageSize,
		'--chat-body-font-size-h6': messageSize,
		'--chat-body-font-size-h5': scaleRem( headingSize, 0.85 ),
		'--chat-body-font-size-h4': scaleRem( headingSize, 0.9 ),
		'--chat-body-font-size-h3': headingSize,
		'--chat-body-font-size-h2': scaleRem( headingSize, 1.1 ),
		'--chat-body-font-size-h1': scaleRem( headingSize, 1.2 ),
		'--chat-body-font-size-code': req( '--chat-font-size-code' ),
		'--chat-font-mono': req( '--chat-font-mono' ),
		'--chat-body-line-height-text': req( '--chat-line-height-text' ),
		'--chat-body-line-height-heading': req( '--chat-line-height-heading' ),
		'--font-size-sm': req( '--chat-font-size-composer' ),
		'--font-size-xs': req( '--chat-font-size-footer' ),
	};

	return expanded;
}

/**
 * Replace the text colour of the filled controls (user bubble, send button,
 * error button) by black or white when the configured pair is below 4.5:1.
 *
 * Works on the merged semantic config, so legacy names and unset tokens are
 * evaluated the way the renderer sees them. Pairs with a transparent,
 * translucent or unparsable colour are left alone. Returns a copy of `stored`
 * and never writes to it.
 *
 * @param stored       Stored sparse style config.
 * @param autoContrast Whether the correction is enabled.
 */
export function applyContrastCorrection(
	stored: Record< string, string >,
	autoContrast: boolean
): Record< string, string > {
	const result = { ...stored };
	if ( ! autoContrast ) {
		return result;
	}

	const merged = {
		...SEMANTIC_STYLE_DEFAULTS,
		...migrateStyleConfig( stored ),
	};

	CONTRAST_PAIRS.filter( ( pair ) => pair.autoCorrect ).forEach( ( pair ) => {
		const fg = parseColor( merged[ pair.fg ] );
		const bg = parseColor( merged[ pair.bg ] );
		if ( ! fg || ! bg || fg.a < 1 || bg.a < 1 ) {
			return;
		}
		if ( contrastRatio( fg, bg ) >= pair.min ) {
			return;
		}
		result[ pair.fg ] = pickReadableText( bg );

		// The send button inherits the bubble colours while its own tokens are
		// unset; pin them first so the corrected bubble text does not leak
		// onto a differently coloured send button.
		if ( pair.fg === '--chat-message-user-fg' ) {
			[
				'--chat-composer-submit-bg',
				'--chat-composer-submit-fg',
			].forEach( ( key ) => {
				if ( ! ( key in result ) && merged[ key ] !== undefined ) {
					result[ key ] = merged[ key ] as string;
				}
			} );
		}
	} );

	return result;
}

/**
 * Parse, migrate and expand a stored style config into the JSON the chat applies.
 *
 * @param raw Stored JSON string.
 */
export function resolveStyleConfigJson( raw: string ): string {
	const stored = parseStyleConfigJson( raw );
	const expanded = expandStyleConfig( stored );
	return JSON.stringify( expanded );
}
